import {
    type ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import { GiphyFetch } from '@giphy/js-fetch-api'
import { Carousel } from '@giphy/react-components'
import styled from 'styled-components'

import { IconCloseLarge, IconSendOutline } from 'assets/svg/sprite'

type GifPopupProps = {
    closeFn: () => void
    onGifClick: (val: any) => void
    bottomOffset?: string
    fixedToViewport?: boolean
}

const GifPopup = ({
    closeFn,
    onGifClick,
    bottomOffset,
    fixedToViewport = false
}: GifPopupProps) => {
    const [text, setText] = useState('')
    const [search, setSearch] = useState('')

    const popupRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleOutsideClick = (e: PointerEvent) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(e.target as Node)
            ) {
                closeFn()
            }
        }

        document.addEventListener('pointerdown', handleOutsideClick)

        return () => {
            document.removeEventListener('pointerdown', handleOutsideClick)
        }
    }, [closeFn])


    const key = process.env.REACT_APP_GIPHY_KEY ?? ''


    const gifFetch = useMemo(() => {
        return new GiphyFetch(key)
    }, [key])

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setSearch(text.trim())
        }, 500)

        return () => {
            window.clearTimeout(timer)
        }
    }, [text])

    const fetchGifs = useCallback(
        async (offset: number) => {
            const query = search.trim()

            if (!query) {
                return await gifFetch.trending({
                    offset,
                    limit: 10
                })
            }

            return await gifFetch.search(query, {
                offset,
                limit: 10
            })
        },
        [gifFetch, search]
    )

    return (
        <Container
            ref={popupRef}
            bottomOffset={bottomOffset}
            fixedToViewport={fixedToViewport}
        >
            <CarouselSlot>
                <CarouselWrapper>
<Carousel
    key={search || 'trending'}
    onGifClick={(gif: any) => {
        onGifClick(gif)
    }}
    gifHeight={115}
    gutter={10}
    borderRadius={20}
    fetchGifs={fetchGifs}
    noResultsMessage={<NoResults>No Results</NoResults>}
    noLink={true}
/>
                </CarouselWrapper>
            </CarouselSlot>

            <InputContainer>
                <InputButtonWrapper>
                    <Input
                        type="text"
                        placeholder="Search Giphy"
                        value={text}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setText(e.target.value)
                        }}
                    />

                    <GiphyClose onClick={closeFn}>
                        <IconCloseLarge />
                    </GiphyClose>
                </InputButtonWrapper>

                <SubmitButton>
                    <IconSendOutline />
                </SubmitButton>
            </InputContainer>
        </Container>
    )
}

const Container = styled.div<{
    bottomOffset?: string
    fixedToViewport?: boolean
}>`
    position: ${({ fixedToViewport }) =>
        fixedToViewport ? 'fixed' : 'absolute'};
bottom: ${({ bottomOffset }) =>
    bottomOffset ?? '6rem'};
    left: 0;
    right: 0;
@media (min-width: 965px) {
    bottom: ${({ bottomOffset }) =>
        bottomOffset ? `calc(${bottomOffset} + 1rem)` : '7rem'};
}
    height: 20rem;
    padding: 1.5rem;

    display: grid;
    grid-template-rows: 12rem 5rem;
    row-gap: 0;

    overflow: hidden;
    box-sizing: border-box;

    background-color: #f2f6f9;
    background: linear-gradient(
            90deg,
            #f6f6f6 0%,
            #ffffff 51.09%,
            #efefef 100%
        ),
        #f2f6f9;

    z-index: 9999;

    font-family: 'Montserrat', sans-serif;

    * {
        box-sizing: border-box;
        font-family: inherit;
    }
`

const CarouselSlot = styled.div`
    width: 100%;
    height: 12rem;
    min-height: 12rem;
    max-height: 12rem;
    overflow: hidden;
`

const CarouselWrapper = styled.div`
    width: 100%;
    height: 12rem;
    min-height: 12rem;
    max-height: 12rem;
    overflow: hidden;
`

const NoResults = styled.div`
    height: 11.5rem;
    min-height: 11.5rem;
    max-height: 11.5rem;

    display: flex;
    align-items: center;

    font-size: 2rem;
    line-height: 2rem;
    color: #778797;
`

const InputContainer = styled.div`
    display: flex;
    align-items: center;

    height: 5rem;
    min-height: 5rem;
    max-height: 5rem;
padding-top:1rem;
    margin: 0;
`

const InputButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 80%;
    position: relative;
    flex: 1 1;
`

const Input = styled.input`
    font-family: 'Montserrat', sans-serif;
    font-style: normal;
    letter-spacing: 0.02em;
    font-size: 1.4rem;
    line-height: 1.7rem;
    background: #fffcfc;
    border: 0.1rem solid rgba(38, 42, 51, 0.2);
    border-radius: 1.5rem;
    box-sizing: border-box;
    font-weight: 500;
    outline: none;
    width: 100%;
    resize: none;
    padding-right: 6.5rem;
    padding-top: 1.4rem;
    padding-left: 1.6rem;
    height: 5rem;
    @media (max-width: 360px) {
        height: 4.5rem;
    }

    padding-bottom: 1.4rem;
`

const GiphyClose = styled.div`
    position: absolute;
    top: 50%;
    right: 2rem;

    display: flex;
    justify-content: center;
    align-items: center;

    transform: translateY(-50%);
    cursor: pointer;
`

const SubmitButton = styled.div`
    cursor: pointer;
    user-select: none;

    width: 5rem;
    min-width: 5rem;
    max-width: 5rem;

    height: 5rem;
    min-height: 5rem;
    max-height: 5rem;
    @media (max-width: 360px) {
        height: 4.5rem;
        min-height:4.5rem;
        width:4.5rem;
        min-width:4.5rem;
    }
    margin-left: 1rem;

    display: flex;
    justify-content: center;
    align-items: center;

    flex-shrink: 0;
    border-radius: 100%;

    border: 0.3rem solid #76bcff;
    background: linear-gradient(92.95deg, #53aff7 -12.15%, #2894ff 111.4%);
    box-shadow: 0 0.4rem 1.4rem rgba(47, 152, 254, 0.5);
`

export default GifPopup